import { supabase } from '../lib/supabase';

/**
 * AI Service for EndoReferral - Uses Supabase Edge Functions with OpenAI
 * All AI operations are secured via Edge Functions to keep API keys server-side
 */

/**
 * Rewrite clinical notes using AI via Edge Function
 * Used in public referral form - secured with referral_id + public_token
 * @param {string} referralId - Referral ID
 * @param {string} publicToken - Public token for validation
 * @param {string} clinicalNotes - Notes to reformulate
 * @param {Array<string>} toothNumbers - Tooth numbers for context
 * @param {Array<string>} referralReasons - Referral reasons for context
 * @returns {Promise<string>} Reformulated text
 */
export async function rewriteNotesViaAI(referralId, publicToken, clinicalNotes, toothNumbers = [], referralReasons = []) {
  try {
    if (!clinicalNotes?.trim()) {
      throw new Error('Les notes cliniques sont vides.');
    }

    console.log('🤖 Calling ai-rewrite-notes Edge Function...');

    const { data, error } = await supabase?.functions?.invoke('ai-rewrite-notes', {
      body: {
        referral_id: referralId,
        public_token: publicToken,
        mode: 'rewrite',
        tooth_numbers: toothNumbers,
        referral_reasons: referralReasons,
        clinical_notes: clinicalNotes,
      },
    });

    if (error) {
      console.error('❌ Edge Function error:', error);
      throw new Error(error.message || 'Erreur lors de la reformulation.');
    }

    if (!data?.text) {
      throw new Error('Aucun texte retourné par l\'IA.');
    }

    console.log('✅ Notes reformulated successfully');
    return data?.text;
  } catch (error) {
    console.error('Error in rewriteNotesViaAI:', error);
    throw new Error(error?.message || 'Erreur lors de la reformulation.');
  }
}

/**
 * Clean up dictated text using AI via Edge Function
 * Used in public referral form - secured with referral_id + public_token
 * @param {string} referralId - Referral ID
 * @param {string} publicToken - Public token for validation
 * @param {string} dictatedText - Dictated text to clean
 * @param {Array<string>} toothNumbers - Tooth numbers for context
 * @param {Array<string>} referralReasons - Referral reasons for context
 * @returns {Promise<string>} Cleaned text
 */
export async function cleanupDictationViaAI(referralId, publicToken, dictatedText, toothNumbers = [], referralReasons = []) {
  try {
    if (!dictatedText?.trim()) {
      throw new Error('Le texte dicté est vide.');
    }

    console.log('🤖 Calling ai-rewrite-notes Edge Function for dictation cleanup...');

    const { data, error } = await supabase?.functions?.invoke('ai-rewrite-notes', {
      body: {
        referral_id: referralId,
        public_token: publicToken,
        mode: 'dictation_cleanup',
        tooth_numbers: toothNumbers,
        referral_reasons: referralReasons,
        dictated_text: dictatedText,
      },
    });

    if (error) {
      console.error('❌ Edge Function error:', error);
      throw new Error(error.message || 'Erreur lors du nettoyage de la dictée.');
    }

    if (!data?.text) {
      throw new Error('Aucun texte retourné par l\'IA.');
    }

    console.log('✅ Dictation cleaned successfully');
    return data?.text;
  } catch (error) {
    console.error('Error in cleanupDictationViaAI:', error);
    throw new Error(error?.message || 'Erreur lors du nettoyage de la dictée.');
  }
}

/**
 * Generate case summary using AI via Edge Function
 * Used in practitioner dashboard - secured with JWT authentication
 * @param {string} referralId - Referral ID
 * @returns {Promise<string>} AI-generated case summary
 */
export async function getCaseSummaryViaAI(referralId) {
  try {
    if (!referralId) {
      throw new Error('ID d\'adressage manquant.');
    }

    // Get current session for JWT token
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.access_token) {
      throw new Error('Session non authentifiée.');
    }

    console.log('🤖 Calling ai-case-summary Edge Function...');

    const { data, error } = await supabase?.functions?.invoke('ai-case-summary', {
      body: {
        referral_id: referralId,
      },
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
      },
    });

    if (error) {
      console.error('❌ Edge Function error:', error);
      throw new Error(error.message || 'Erreur lors de la génération du résumé.');
    }

    if (!data?.summary) {
      throw new Error('Aucun résumé retourné par l\'IA.');
    }

    console.log('✅ Case summary generated successfully');
    return data?.summary;
  } catch (error) {
    console.error('Error in getCaseSummaryViaAI:', error);
    throw new Error(error?.message || 'Erreur lors de la génération du résumé.');
  }
}

export default {
  rewriteNotesViaAI,
  cleanupDictationViaAI,
  getCaseSummaryViaAI,
};